/**
 * PrizePool - 奖品池组件
 *
 * 展示所有可获得的奖品列表,支持横向滚动和鼠标拖拽。
 * 基于 Figma 设计稿实现响应式布局。
 *
 * Figma 设计稿: https://www.figma.com/design/DhWsPamqTM76eMHS0aIQqw
 * 设计节点: 498:6894 (Prize Pool section)
 *
 * @module PrizePool
 * @date 2026-01-30
 */

'use client'

import React, { useState, useCallback, useEffect, useRef } from 'react'
import { cn } from '../../helpers/index.js'
import type { Prize, WinningInfo, PrizePoolProps } from './types.js'
import { Heading, Picture } from '../../components/index.js'
import { useDraggableScroll } from '../../hooks/useDraggableScroll.js'

// ============================================================================
// 惯性滚动常量
// ============================================================================

const MOMENTUM_FRICTION = 0.95 // 摩擦力系数
const MIN_VELOCITY = 0.1 // 最小速度阈值
const FRAME_TIME = 16 // 16ms per frame (60fps)

// ============================================================================
// PrizePool 组件
// ============================================================================

/**
 * PrizePool - 奖品池展示组件
 *
 * 特性:
 * - 横向滚动列表(mobile/tablet)
 * - 鼠标拖拽滚动（基于 Pointer Events API）
 * - 触摸友好
 * - 边缘渐隐效果
 * - 显示奖品图片、排名(1st/2nd/3rd)和名称
 * - 支持响应式布局
 * - 显示中奖人名单(自动滚动循环)
 * - 完整的惯性滚动动画
 */
export const PrizePool: React.FC<PrizePoolProps> = ({
  theme,
  prizes,
  title = 'Prize Pool',
  winningInfos,
  className,
}) => {
  // Hooks 必须在条件判断之前调用
  // 拖拽滚动 Hook
  const { containerRef, canScrollLeft, canScrollRight } = useDraggableScroll({
    dragSpeed: 1.2,
    enableMomentum: true,
    dragElastic: 0.1,
  })

  // 拖拽状态 - 使用 ref 避免闭包问题
  const isDraggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)

  // 拖拽相关 refs
  const initialScrollLeftRef = useRef(0)
  const initialPointerXRef = useRef(0)
  const velocityRef = useRef(0)
  const lastPointerXRef = useRef(0)
  const lastTimeRef = useRef(0)
  const momentumAnimationRef = useRef<number | null>(null)

  // 处理拖拽开始
  const handleDragStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const container = containerRef.current
      if (!container) return

      // 使用 setPointerCapture 确保即使鼠标移出容器，事件仍能触发
      e.currentTarget.setPointerCapture(e.pointerId)

      isDraggingRef.current = true
      setIsDragging(true)
      initialScrollLeftRef.current = container.scrollLeft
      initialPointerXRef.current = e.clientX
      lastPointerXRef.current = e.clientX
      lastTimeRef.current = Date.now()
      velocityRef.current = 0

      // 取消正在进行的惯性滚动
      if (momentumAnimationRef.current !== null) {
        cancelAnimationFrame(momentumAnimationRef.current)
        momentumAnimationRef.current = null
      }

      // 只阻止文本选择，不阻止 pointer 事件
      document.body.style.userSelect = 'none'
    },
    [containerRef]
  )

  // 处理拖拽中
  const handleDragMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return

      const container = containerRef.current
      if (!container) return

      const currentX = e.clientX
      const deltaPixels = initialPointerXRef.current - currentX

      // 计算速度（用于惯性滚动）
      const now = Date.now()
      const timeDelta = Math.max(now - lastTimeRef.current, 1)
      velocityRef.current = ((currentX - lastPointerXRef.current) / timeDelta) * -1

      lastPointerXRef.current = currentX
      lastTimeRef.current = now

      // 更新滚动位置
      const newScrollLeft = initialScrollLeftRef.current + deltaPixels
      const maxScroll = container.scrollWidth - container.clientWidth

      // 边界约束 + 越界阻力
      if (newScrollLeft < 0) {
        container.scrollLeft = newScrollLeft * 0.5
      } else if (newScrollLeft > maxScroll) {
        const overshoot = newScrollLeft - maxScroll
        container.scrollLeft = maxScroll + overshoot * 0.5
      } else {
        container.scrollLeft = newScrollLeft
      }

      // 只在浏览器环境触发 scroll 事件
      if (typeof window !== 'undefined') {
        container.dispatchEvent(new Event('scroll', { bubbles: true }))
      }
    },
    [containerRef]
  )

  // 惯性滚动动画 - 必须在 handleDragEnd 之前定义
  const animateMomentumScroll = useCallback((container: HTMLDivElement, initialVelocity: number) => {
    let velocity = initialVelocity
    const maxScroll = container.scrollWidth - container.clientWidth

    const animate = () => {
      velocity *= MOMENTUM_FRICTION

      if (Math.abs(velocity) < MIN_VELOCITY) {
        momentumAnimationRef.current = null
        return
      }

      const newScroll = container.scrollLeft + velocity * FRAME_TIME

      if (newScroll < 0) {
        container.scrollLeft = 0
        momentumAnimationRef.current = null
        return
      }

      if (newScroll > maxScroll) {
        container.scrollLeft = maxScroll
        momentumAnimationRef.current = null
        return
      }

      container.scrollLeft = newScroll
      // 只在浏览器环境触发 scroll 事件
      if (typeof window !== 'undefined') {
        container.dispatchEvent(new Event('scroll', { bubbles: true }))
      }

      momentumAnimationRef.current = requestAnimationFrame(animate)
    }

    momentumAnimationRef.current = requestAnimationFrame(animate)
  }, [])

  // 处理拖拽结束
  const handleDragEnd = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return

      // 释放 pointer capture
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }

      isDraggingRef.current = false
      setIsDragging(false)

      const container = containerRef.current
      if (!container) return

      // 恢复文本选择
      document.body.style.userSelect = 'auto'

      // 启动惯性滚动
      if (Math.abs(velocityRef.current) > MIN_VELOCITY) {
        animateMomentumScroll(container, velocityRef.current)
      }
    },
    [containerRef, animateMomentumScroll]
  )

  // 清理：组件卸载时取消动画
  useEffect(() => {
    return () => {
      if (momentumAnimationRef.current !== null) {
        cancelAnimationFrame(momentumAnimationRef.current)
      }
    }
  }, [])

  // 防御性编程:处理 undefined 或空数组
  if (!prizes || prizes.length === 0) {
    return null
  }

  return (
    <div className={cn('prize-pool-content', theme === 'dark' ? 'aiui-dark' : '', className)}>
      <div
        className={cn(
          'border-b ',
          'py-[16px]',
          'desktop:pt-[24px]',
          theme === 'dark' ? 'border-[#3A3C40]' : 'border-[#dadce0]'
        )}
      >
        <div
          className={cn(
            'prize-pool-container',
            'mb-4 px-[16px]',
            'desktop:px-[24px]',
            'lg-desktop:px-[32px]',
            className
          )}
        >
          {/* 标题 */}
          <Heading size={2} className={cn('prize-pool-title', 'text-info-primary')}>
            {title}
          </Heading>
        </div>

        {/* 奖品列表 - 支持拖拽滚动 */}
        <div className="relative">
          {/* 左边缘渐隐遮罩 */}
          {canScrollLeft && (
            <div
              className={cn(
                'pointer-events-none',
                'absolute inset-y-0 left-0 z-10',
                'desktop:w-[48px] w-[32px]',
                theme === 'dark'
                  ? 'bg-gradient-to-r from-[#1D1D1F] to-transparent'
                  : 'bg-gradient-to-r from-[#EAEAEC] to-transparent'
              )}
              aria-hidden="true"
            />
          )}

          {/* 右边缘渐隐遮罩 */}
          {canScrollRight && (
            <div
              className={cn(
                'pointer-events-none',
                'absolute inset-y-0 right-0 z-10',
                'desktop:w-[48px] w-[32px]',
                theme === 'dark'
                  ? 'bg-gradient-to-l from-[#1D1D1F] to-transparent'
                  : 'bg-gradient-to-l from-[#EAEAEC] to-transparent'
              )}
              aria-hidden="true"
            />
          )}

          {/* 滚动容器 */}
          <div
            ref={containerRef}
            className={cn(
              'px-[16px]',
              'desktop:px-[24px]',
              'lg-desktop:px-[32px]',
              'overflow-x-auto overflow-y-hidden',
              'scrollbar-hide',
              isDragging && 'cursor-grabbing'
            )}
            style={{
              WebkitOverflowScrolling: 'touch', // iOS 平滑滚动
              scrollBehavior: 'auto',
            }}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerLeave={handleDragEnd}
            onPointerCancel={handleDragEnd}
          >
            <div
              className={cn(
                'prize-pool-list',
                'flex items-center gap-[16px]',
                'cursor-grab active:cursor-grabbing',
                'select-none'
              )}
            >
              {prizes.map((prize, index) => (
                <React.Fragment key={prize.prizeKey}>
                  <PrizePoolItem prize={prize} />
                  {/* 横向分隔线 */}
                  {index < prizes.length - 1 && (
                    <div
                      className={cn(
                        'prize-divider',
                        'lg-desktop:h-[54px]',
                        'h-[48px] w-0',
                        'border-l',
                        theme === 'dark' ? 'border-[#3A3C40]' : 'border-[#dadce0]',
                        'shrink-0'
                      )}
                      aria-hidden="true"
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 中奖人名单 - 自动滚动 */}
      {winningInfos && winningInfos.length > 0 && <WinnersList winningInfos={winningInfos} />}
    </div>
  )
}

// ============================================================================
// PrizePoolItem 子组件 - 单个奖品卡片
// ============================================================================

interface PrizePoolItemProps {
  prize: Prize
}

/**
 * PrizePoolItem - 奖品池中的单个奖品卡片
 *
 * 展示奖品图片、排名和名称,带悬停效果。
 */
const PrizePoolItem: React.FC<PrizePoolItemProps> = ({ prize }) => {
  return (
    <div
      className={cn(
        'prize-pool-item',
        'flex items-start gap-[8px]',
        'shrink-0',
        'h-[80px] w-[280px]',
        'desktop:h-[96px] desktop:w-[320px]',
        'lg-desktop:h-[108px]'
      )}
    >
      {/* 奖品图片 */}
      <Picture
        source={prize.image.url}
        alt={prize.image.alt || `奖品: ${prize.name}${prize.rank ? `, 排名 ${prize.rank}` : ''}`}
        className={cn(
          'prize-pool-image',
          'size-[80px]',
          'desktop:size-[96px]',
          'lg-desktop:size-[108px]',
          'flex-none rounded-lg object-cover',
          'pointer-events-none' // 防止图片拖拽干扰
        )}
      />

      {/* 奖品信息 */}
      <div
        className={cn(
          'prize-pool-info',
          'flex flex-1 flex-col items-start justify-end',
          'text-info-primary',
          'min-w-0' // 确保文本可以被截断
        )}
      >
        {/* 排名标识(1st/2nd/3rd,可选) */}
        <p
          className={cn(
            'prize-rank',
            'h-[20px] text-[14px] font-bold leading-[1.2] ',
            'desktop:h-[22px] desktop:text-[16px]',
            'lg-desktop:h-[25px] lg-desktop:text-[18px]',
            {
              'text-[#FF6B00]': prize.rank === '1st' || prize.rank === '2nd' || prize.rank === '3rd', // 橙色
              // 'text-[#767880]': prize.rank === 'others', // 灰色
            }
          )}
        >
          {prize?.rank || ''}
        </p>

        {/* 奖品名称 - 最多显示 2 行,超出显示省略号 */}
        <p
          className={cn(
            'prize-pool-name',
            'text-[14px] font-semibold leading-[1.4] ',
            'desktop:text-[16px]',
            'lg-desktop:text-[18px]',
            'line-clamp-2 overflow-hidden text-ellipsis', // 最多 2 行
            'text-info-primary',
            'w-full'
          )}
          title={prize.name} // 悬停显示完整名称
        >
          {prize.name}
        </p>
      </div>
    </div>
  )
}

// ============================================================================
// WinnersList 子组件 - 中奖人名单自动滚动(使用 Swiper)
// ============================================================================

interface WinnersListProps {
  winningInfos: WinningInfo[]
}

/**
 * WinnersList - 中奖人名单滚动组件
 *
 * 使用纯 CSS 动画实现跑马灯效果
 *
 * 特性:
 * - 纯 CSS keyframe 动画，无 JavaScript 计算
 * - 轻量级实现，无需第三方库
 * - 响应式设计
 * - 线性滚动，流畅自然
 */
const WinnersList: React.FC<WinnersListProps> = ({ winningInfos }) => {
  // 复制内容以实现无缝循环
  // 只有4条记录时不复制,静态显示
  // 有5条或更多记录时,复制1次以实现无缝滚动
  const duplicatedInfos = winningInfos.length <= 4 ? winningInfos : [...winningInfos, ...winningInfos]

  // 是否启用滚动动画(只有多条记录时启用)
  const enableAnimation = winningInfos.length > 2

  return (
    <div className={cn('winners-list-wrapper', 'py-[12px]', 'laptop:py-[16px]', 'overflow-hidden')}>
      <div
        className={cn('winners-list-scroll', 'flex gap-[32px]', enableAnimation ? 'animate-marquee-x px-0' : 'px-4')}
      >
        {duplicatedInfos.map((winner, index) => (
          <div
            key={`${winner.winningKey}-${index}`}
            className={cn(
              'winner-item',
              'flex items-center gap-[8px]',
              'whitespace-nowrap',
              'text-[14px] font-semibold text-[#767880]',
              'desktop:text-[16px]',
              'lg-desktop:text-[18px]'
            )}
          >
            <span className="winner-name">{winner.userName}</span>
            <span className="winner-text">has won the</span>
            <span className="winner-prize">{winner.prizeName}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// 默认导出
// ============================================================================

export default PrizePool
